import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Globe, ShieldCheck, Clock, AlertTriangle, PauseCircle } from "lucide-react";

/**
 * Additional Website Hosting management widget (Phase D UI).
 *
 * Renders the hosting state per business:
 *   - Website #1 → Hosted — Included with your plan (no CTA)
 *   - Website #2+ no sub → Hosting Required + [Add Hosting — $50/month]
 *   - Grace period → Hosting transition period · Live until [date] + CTA
 *   - Active → Hosted — Additional Hosting Active + [Manage Hosting]
 *   - Canceling → Hosting active until [date] + [Reactivate]
 *   - Expired/canceled → Hosting Required + [Reactivate Hosting]
 */
export default function WebsiteHostingTab() {
  const [rows, setRows] = useState([]);
  const [monthly, setMonthly] = useState(50.0);
  const [firstId, setFirstId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/websites/hosting/status");
      setRows(data.websites || []);
      setMonthly(data.monthly_usd || 50);
      setFirstId(data.first_website_id || null);
    } catch {
      toast.error("Couldn't load hosting status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addHosting = async (bizId) => {
    setBusyId(bizId);
    try {
      const { data } = await api.post(
        "/websites/hosting/checkout",
        { business_id: bizId, origin_url: window.location.origin },
      );
      window.location.href = data.url;
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "hosting_already_active") toast.info("Hosting is already active for this website.");
      else toast.error("Couldn't start hosting checkout");
      setBusyId(null);
    }
  };

  const cancel = async (bizId) => {
    if (!confirm("Cancel hosting at period end? The site will remain live until the paid-through date.")) return;
    setBusyId(bizId);
    try {
      await api.post("/websites/hosting/cancel", { business_id: bizId });
      toast.success("Hosting will end at the current period end");
      await load();
    } catch {
      toast.error("Couldn't schedule cancel");
    } finally { setBusyId(null); }
  };

  const reactivate = async (bizId) => {
    setBusyId(bizId);
    try {
      await api.post("/websites/hosting/reactivate", { business_id: bizId });
      toast.success("Hosting reactivated");
      await load();
    } catch {
      toast.error("Couldn't reactivate");
    } finally { setBusyId(null); }
  };

  if (loading) {
    return <div className="nb-card p-5 text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading hosting status…</div>;
  }

  return (
    <div data-testid="hosting-widget">
      <div className="nb-card p-5 mb-4">
        <div className="font-display text-xl font-extrabold flex items-center gap-2">
          <Globe className="w-5 h-5" /> Additional Website Hosting
        </div>
        <div className="text-sm text-gray-600 mt-1">
          Website #1 is included with your active paid plan. Each additional live website is
          <b> ${monthly}/month</b>. Cancel anytime — sites keep serving until the paid-through date.
        </div>
      </div>
      <div className="grid gap-3">
        {rows.map((row) => (
          <HostingRow key={row.business_id}
                      row={row}
                      monthly={monthly}
                      firstId={firstId}
                      busy={busyId === row.business_id}
                      onAdd={() => addHosting(row.business_id)}
                      onCancel={() => cancel(row.business_id)}
                      onReactivate={() => reactivate(row.business_id)} />
        ))}
        {rows.length === 0 && (
          <div className="nb-card p-4 text-sm text-gray-500">You don&apos;t have any websites yet.</div>
        )}
      </div>
    </div>
  );
}

function HostingRow({ row, monthly, firstId, busy, onAdd, onCancel, onReactivate }) {
  const h = row.hosting || {};
  const isFirst = row.business_id === firstId;
  const st = _stateFor(h, isFirst);

  return (
    <div className="nb-card p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`hosting-row-${row.business_id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate">{row.name || row.slug || row.business_id}</div>
        <div className="text-xs text-gray-500 truncate">/{row.slug}</div>
        <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${st.pillClass}`}
             data-testid={`hosting-state-${row.business_id}`}>
          <st.Icon className="w-3 h-3" /> {st.label}
        </div>
        {st.subline && <div className="text-[11px] text-gray-500 mt-1">{st.subline}</div>}
      </div>
      <div className="flex items-center gap-2">
        {st.primary === "add" && (
          <button onClick={onAdd} disabled={busy}
                  data-testid={`hosting-add-${row.business_id}`}
                  className="px-3 py-2 text-xs font-bold rounded-xl border-2 border-black bg-[#8B5CF6] text-white nb-press"
                  style={{ boxShadow: "3px 3px 0 0 #111111" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add Hosting — $${monthly}/month`}
          </button>
        )}
        {st.primary === "manage" && (
          <button onClick={onCancel} disabled={busy}
                  data-testid={`hosting-cancel-${row.business_id}`}
                  className="px-3 py-2 text-xs font-bold rounded-xl border-2 border-black bg-white nb-press"
                  style={{ boxShadow: "3px 3px 0 0 #111111" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel Hosting"}
          </button>
        )}
        {st.primary === "reactivate" && (
          <button onClick={onReactivate} disabled={busy}
                  data-testid={`hosting-reactivate-${row.business_id}`}
                  className="px-3 py-2 text-xs font-bold rounded-xl border-2 border-black bg-[#10B981] text-white nb-press"
                  style={{ boxShadow: "3px 3px 0 0 #111111" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reactivate Hosting"}
          </button>
        )}
      </div>
    </div>
  );
}

function _stateFor(h, isFirst) {
  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(); } catch { return ""; }
  };
  if (isFirst) {
    return { label: "Hosted — Included with your plan", Icon: ShieldCheck,
             pillClass: "text-emerald-700 bg-emerald-50 border border-emerald-200",
             subline: null, primary: null };
  }
  if (h.grace) {
    return { label: "Hosting transition period", Icon: Clock,
             pillClass: "text-amber-800 bg-amber-50 border border-amber-200",
             subline: `Live until ${fmt(h.grace_until)} unless hosting is added.`,
             primary: "add" };
  }
  if (h.status === "active" || h.status === "trialing") {
    if (h.cancel_at_period_end) {
      return { label: "Hosting canceling at period end", Icon: Clock,
               pillClass: "text-amber-800 bg-amber-50 border border-amber-200",
               subline: `Hosting active until ${fmt(h.current_period_end)}.`,
               primary: "reactivate" };
    }
    return { label: "Hosted — Additional Hosting Active", Icon: ShieldCheck,
             pillClass: "text-emerald-700 bg-emerald-50 border border-emerald-200",
             subline: h.current_period_end ? `Renews ${fmt(h.current_period_end)}.` : null,
             primary: "manage" };
  }
  if (h.status === "past_due") {
    return { label: "Hosting past due", Icon: AlertTriangle,
             pillClass: "text-rose-700 bg-rose-50 border border-rose-200",
             subline: "Update your payment method to keep this site live.",
             primary: "add" };
  }
  if (h.status === "canceled") {
    return { label: "Hosting Required", Icon: PauseCircle,
             pillClass: "text-slate-700 bg-slate-100 border border-slate-200",
             subline: "This site is paused. Reactivate hosting to make it live again.",
             primary: "reactivate" };
  }
  // No sub at all → Not hosted
  return { label: "Hosting Required", Icon: PauseCircle,
           pillClass: "text-slate-700 bg-slate-100 border border-slate-200",
           subline: `Add hosting to publish this website — $${50}/month.`,
           primary: "add" };
}
